import { IonButton, IonContent } from '@ionic/react';
import { Player } from '@lottiefiles/react-lottie-player';
import React from 'react';

import { UiTypography } from '@/components/ui/Typography';
import { LAYOUT_NOTES } from '@/constants/campaign-notes';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle } from '../../components/Title';

import { ScreenSecurityStyles } from './Security.styles';

export const ScreenSecurity: React.FC = () => {
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Security',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const layoutNotes = webCampaign.getLayoutNotes();
  const removeDialogueReferences = layoutNotes.includes(
    LAYOUT_NOTES.REMOVE_DIALOGUE_REFERENCES,
  );
  const requireTermsCheckbox = layoutNotes.includes(
    LAYOUT_NOTES.REQUIRE_TERMS_CHECKBOX,
  );

  const handleNextStep = (): void => {
    wizard.nextStep();
  };

  const handleBackStep = (): void => {
    wizard.goToStep('major_turn_off');
  };

  const getSecurityTitle = () => {
    if (webCampaign.isLayoutLure())
      return 'Lure AI prioritizes and protects your personal information';
    if (removeDialogueReferences)
      return 'We prioritize and protect your personal information';
    return 'Dialogue prioritizes and protects your personal information';
  };

  const getPrivacySlogan = () => {
    if (webCampaign.isLayoutLure())
      return 'What happens in Lure AI, stays in Lure AI';
    if (removeDialogueReferences) return 'What happens inside, stays inside';
    return 'What happens in Dialogue, stays in Dialogue';
  };

  const securityTitle = getSecurityTitle();
  const privacySlogan = getPrivacySlogan();

  const privacyLink = webCampaign.getLegalPrivacyLink();
  const termsLink = webCampaign.getLegalTermsLink();

  const isButtonDisabled = requireTermsCheckbox && !termsAccepted;

  return (
    <ScreenSecurityStyles>
      <OnboardingNavigation onBack={handleBackStep} />

      <OwnAiTitle>{securityTitle}</OwnAiTitle>
      <IonContent scrollY={false}>
        <div className="description">
          <OwnAiTitle>{privacySlogan}</OwnAiTitle>
        </div>

        <Player
          src="https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/lock-animation-dark.json"
          autoplay
          loop={false}
          keepLastFrame
          className="animation"
        />

        <UiTypography className="disclaimer" variant="body-small">
          We never disclose your information.{' '}
          {requireTermsCheckbox ? (
            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="terms-checkbox"
              />
              <span>
                I agree with{' '}
                <a
                  href={privacyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href={termsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                >
                  Terms of Use
                </a>
              </span>
            </label>
          ) : (
            <>
              By tapping on Continue, you agree with our
              <IonButton
                mode="ios"
                className="link"
                href={privacyLink}
                target="_blank"
              >
                Privacy Policy
              </IonButton>{' '}
              and
              <IonButton
                mode="ios"
                className="link"
                href={termsLink}
                target="_blank"
              >
                Terms of Use
              </IonButton>
            </>
          )}
        </UiTypography>
      </IonContent>

      <OnboardingFooter
        onContinue={handleNextStep}
        disabled={isButtonDisabled}
      />
    </ScreenSecurityStyles>
  );
};
